import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';

// Run status configurations
const STATUS_CONFIG = {
  SUCCESS: { color: 'bg-green-500', text: 'text-green-700 dark:text-green-400', label: 'Success' },
  FAILURE: { color: 'bg-red-500', text: 'text-red-700 dark:text-red-400', label: 'Failed' },
  CANCELED: { color: 'bg-gray-500', text: 'text-gray-700 dark:text-gray-400', label: 'Canceled' },
  STARTED: { color: 'bg-blue-500', text: 'text-blue-700 dark:text-blue-400', label: 'Running' },
  QUEUED: { color: 'bg-yellow-500', text: 'text-yellow-700 dark:text-yellow-400', label: 'Queued' },
  NOT_STARTED: { color: 'bg-gray-300', text: 'text-gray-500', label: 'Not Started' }
};

const PipelineStatus = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState(null);
  const [runs, setRuns] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [models, setModels] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [triggeringJob, setTriggeringJob] = useState(null);

  // Fetch all pipeline data
  const fetchData = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      
      const [statusRes, runsRes, jobsRes, modelsRes] = await Promise.all([
        api.get('/dbt/status').catch(() => ({ data: {} })),
        api.get('/dbt/runs?limit=10').catch(() => ({ data: { runs: [] } })),
        api.get('/dbt/jobs').catch(() => ({ data: { jobs: [] } })),
        api.get('/dbt/models').catch(() => ({ data: { models: [] } }))
      ]);
      
      setStatus(statusRes.data);
      setRuns(runsRes.data.runs || []);
      setJobs(jobsRes.data.jobs || []);
      setModels(modelsRes.data.models || []);
    } catch (err) {
      setError('Failed to load pipeline status');
      console.error(err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Trigger a dbt run
  const handleTriggerRun = async (type = 'build') => {
    try {
      setTriggeringJob(type);
      setError(null);
      
      let response;
      switch (type) {
        case 'staging':
          response = await api.post('/dbt/staging');
          break;
        case 'build':
          response = await api.post('/dbt/build');
          break;
        case 'test':
          response = await api.post('/dbt/test');
          break;
        case 'compile':
          response = await api.post('/dbt/compile');
          break;
        default:
          response = await api.post('/dbt/run');
      }
      
      setSuccess(`${type} run triggered successfully`);
      setTimeout(() => fetchData(true), 2000);
    } catch (err) {
      setError(`Failed to trigger ${type} run`);
      console.error(err);
    } finally {
      setTriggeringJob(null);
    }
  };

  // Trigger a Dagster job
  const handleTriggerDagsterJob = async (jobName) => {
    try {
      setTriggeringJob(jobName);
      setError(null);
      
      await api.post('/dbt/dagster/job', { jobName });
      setSuccess(`Dagster job '${jobName}' triggered`);
      setTimeout(() => fetchData(true), 2000);
    } catch (err) {
      setError(`Failed to trigger Dagster job`);
      console.error(err);
    } finally {
      setTriggeringJob(null);
    }
  };

  // Format timestamp
  const formatTime = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  // Calculate duration
  const formatDuration = (start, end) => {
    if (!start || !end) return 'N/A';
    const duration = end - start;
    if (duration < 60) return `${duration}s`;
    if (duration < 3600) return `${Math.floor(duration / 60)}m ${duration % 60}s`;
    return `${Math.floor(duration / 3600)}h ${Math.floor((duration % 3600) / 60)}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Pipeline Status
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Monitor dbt runs and Dagster orchestration
          </p>
        </div>
        <button
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="px-4 py-2 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg flex items-center gap-2"
        >
          <svg className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Setup Required Banner */}
      {status && !status.dagster?.available && (
        <div className="mb-6 p-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-amber-100 dark:bg-amber-800 rounded-lg">
              <svg className="w-6 h-6 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-300 mb-2">
                Pipeline Services Not Running
              </h3>
              <p className="text-amber-700 dark:text-amber-400 mb-4">
                Dagster orchestration is not detected. dbt operations are managed through Dagster, which runs in Docker.
              </p>
              <div className="bg-gray-900 rounded-lg p-4 font-mono text-sm text-green-400 overflow-x-auto">
                <p className="text-gray-500 mb-2"># Start the pipeline services:</p>
                <p>docker compose --profile pipeline up -d</p>
              </div>
              <div className="mt-4 flex flex-col gap-2 text-sm">
                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <span className="font-medium">Dagster URL:</span>
                  <code className="px-2 py-1 bg-amber-100 dark:bg-amber-800/50 rounded">
                    {status.dagster?.url || 'http://localhost:3030'}
                  </code>
                </div>
                {status.dbt?.note && (
                  <p className="text-amber-600 dark:text-amber-400 text-xs mt-2">
                    ℹ️ {status.dbt.note}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Alerts */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400">
          {error}
          <button onClick={() => setError(null)} className="float-right">&times;</button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-400">
          {success}
          <button onClick={() => setSuccess(null)} className="float-right">&times;</button>
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {/* Dagster Status */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Dagster</h3>
            <div className={`w-3 h-3 rounded-full ${status?.dagster?.available ? 'bg-green-500' : 'bg-red-500'}`}></div>
          </div>
          <p className={`text-lg font-semibold ${status?.dagster?.available ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {status?.dagster?.available ? 'Connected' : 'Disconnected'}
          </p>
          {status?.dagster?.error && (
            <p className="text-xs text-red-500 mt-1 truncate" title={status.dagster.error}>{status.dagster.error}</p>
          )}
        </div>

        {/* dbt Status (via Dagster) */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">dbt</h3>
            <div className={`w-3 h-3 rounded-full ${status?.dbt?.available ? 'bg-green-500' : 'bg-gray-400'}`}></div>
          </div>
          <p className={`text-lg font-semibold ${status?.dbt?.available ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-gray-400'}`}>
            {status?.dbt?.available ? 'Ready' : 'Via Dagster'}
          </p>
          <p className="text-xs text-gray-500 mt-1">Managed by Dagster</p>
        </div>

        {/* Recent Runs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Recent Runs</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{runs.length}</p>
          <p className="text-xs text-gray-500 mt-1">Last 24 hours</p>
        </div>

        {/* dbt Models */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-5">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">dbt Models</h3>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{models.length}</p>
          <p className="text-xs text-gray-500 mt-1">Available assets</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Quick Actions</h2>
        </div>
        <div className="p-4 flex flex-wrap gap-3">
          <ActionButton
            label="Run Full Build"
            icon="play"
            onClick={() => handleTriggerRun('build')}
            loading={triggeringJob === 'build'}
            color="green"
          />
          <ActionButton
            label="Run Staging"
            icon="database"
            onClick={() => handleTriggerRun('staging')}
            loading={triggeringJob === 'staging'}
            color="blue"
          />
          <ActionButton
            label="Run Tests"
            icon="check"
            onClick={() => handleTriggerRun('test')}
            loading={triggeringJob === 'test'}
            color="purple"
          />
          <ActionButton
            label="Compile Project"
            icon="code"
            onClick={() => handleTriggerRun('compile')}
            loading={triggeringJob === 'compile'}
            color="gray"
          />
        </div>
      </div>

      {/* Recent Runs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Runs</h2>
        </div>
        
        {runs.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <p>No recent runs found.</p>
            <p className="text-sm mt-1">Runs will appear here after triggering dbt or Dagster jobs.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Run ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Pipeline
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Started
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Duration
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Tags
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {runs.map((run) => {
                  const statusConfig = STATUS_CONFIG[run.status] || STATUS_CONFIG.NOT_STARTED;
                  return (
                    <tr key={run.runId}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-mono text-gray-900 dark:text-white">
                          {run.runId?.substring(0, 8)}...
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm text-gray-600 dark:text-gray-300">
                          {run.pipelineName || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.text} bg-opacity-20`}>
                          <span className={`w-2 h-2 rounded-full ${statusConfig.color} mr-1.5`}></span>
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatTime(run.startTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDuration(run.startTime, run.endTime)}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {run.tags?.slice(0, 3).map((tag, i) => (
                            <span key={i} className="px-2 py-0.5 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                              {tag.key}: {tag.value}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Available Jobs */}
      {jobs.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dagster Jobs</h2>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {jobs.map((job, index) => (
              <div key={index} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900 dark:text-white">{job.name}</h3>
                  <button
                    onClick={() => handleTriggerDagsterJob(job.name)}
                    disabled={triggeringJob === job.name}
                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                  >
                    {triggeringJob === job.name ? (
                      <span className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full block"></span>
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {job.description || 'No description'}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* dbt Models List */}
      {models.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">dbt Models</h2>
          </div>
          <div className="p-4">
            <div className="flex flex-wrap gap-2">
              {models.map((model, index) => (
                <span
                  key={index}
                  className={`px-3 py-1 text-sm rounded-full ${getModelTypeColor(model.type || 'model')}`}
                >
                  {model.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Dagster UI Link */}
      {status?.dagster?.available && (
        <div className="mt-8 text-center">
          <a
            href="http://localhost:3030"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            Open Dagster UI for detailed pipeline management
          </a>
        </div>
      )}
    </div>
  );
};

// Action Button Component
const ActionButton = ({ label, icon, onClick, loading, color }) => {
  const colors = {
    green: 'bg-green-600 hover:bg-green-700 text-white',
    blue: 'bg-blue-600 hover:bg-blue-700 text-white',
    purple: 'bg-purple-600 hover:bg-purple-700 text-white',
    gray: 'bg-gray-600 hover:bg-gray-700 text-white'
  };

  const icons = {
    play: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
    ),
    database: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
    ),
    check: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    ),
    code: (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
    )
  };

  return (
    <button
      onClick={onClick}
      disabled={loading}
      className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 ${colors[color]}`}
    >
      {loading ? (
        <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {icons[icon]}
        </svg>
      )}
      {label}
    </button>
  );
};

// Get model type color
const getModelTypeColor = (type) => {
  const colors = {
    model: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    source: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    seed: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    snapshot: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
  };
  return colors[type] || colors.model;
};

export default PipelineStatus;

